import { CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { COUNT_CAP } from '@omnea-app/packages/backend/db/cappedCount'
import { useMuiDataGridLocaleText } from './hooks/useMuiDataGridLocaleText'

type LabelInfo = { from: number; to: number; count: number; page: number }

type Props = LabelInfo & {
  totalCountLoading: boolean
  totalCountIsApprox: boolean
  // The "at least" total to show when approximate: MUI's own count is forced to -1 (unknown) to keep
  // prev/next enabled, so the real figure is threaded separately. A capped count passes COUNT_CAP.
  approxTotalCount?: number
}

type MuiTablePaginationLocale = {
  labelDisplayedRows?: (info: LabelInfo) => React.ReactNode
}

export const PaginationLabel: React.FC<Props> = ({
  from,
  to,
  count,
  page,
  totalCountLoading,
  totalCountIsApprox,
  approxTotalCount,
}) => {
  const { t } = useTranslation('common')
  const localeText = useMuiDataGridLocaleText()

  if (totalCountLoading) {
    return (
      <>
        {t('table.table-footer.total-count', { from, to })} <CircularProgress size={12} sx={{ verticalAlign: 'middle', m: 2 }} />
      </>
    )
  }

  if (totalCountIsApprox) {
    return <>{t('table.table-footer.total-count-approx', { from, to, count: approxTotalCount ?? COUNT_CAP })}</>
  }

  if (!Number.isFinite(count) || count < 0) return null
  const muiLocale = (localeText.MuiTablePagination ?? {}) as MuiTablePaginationLocale
  return <>{muiLocale.labelDisplayedRows?.({ from, to, count, page }) ?? `${from}–${to} of ${count}`}</>
}
